import { inject, Injectable } from '@angular/core';
import {
  patientAlertsActions,
  selectEditedPatientAlertsItemId,
  selectIsPatientAlertsLoading,
  selectNewPatientAlertsItem,
  selectPatientAlertsItems,
} from '@libs/store';
import { Store } from '@ngrx/store';
import { ProvisionalStatus } from '@ffd/api-client';
import { DateTime } from 'luxon';
import { HealthHistoryDialogService } from '../../health-history-dialog.service';
import { PatientAlertsItemSaveData } from '../patient-alerts-item-edit/patient-alerts-item-edit.component';

@Injectable()
export class PatientAlertsTabService {
  private readonly store = inject(Store);
  private readonly healthHistoryDialogService = inject(HealthHistoryDialogService);

  private readonly siteId = this.healthHistoryDialogService.siteIdSignal;
  private readonly patientId = this.healthHistoryDialogService.patientIdSignal;

  public readonly patientAlerts = this.store.selectSignal(selectPatientAlertsItems);
  public readonly isLoading = this.store.selectSignal(selectIsPatientAlertsLoading);
  public readonly editedPatientAlertsItemId = this.store.selectSignal(selectEditedPatientAlertsItemId);
  public readonly newPatientAlertsItem = this.store.selectSignal(selectNewPatientAlertsItem);

  public init(): void {
    this.store.dispatch(
      patientAlertsActions.getPatientAlerts({
        siteId: this.siteId(),
        patientId: this.patientId(),
      }),
    );
  }

  public setEditMode(id: number | null): void {
    this.store.dispatch(patientAlertsActions.setEditedPatientAlertsItemId({ id }));
  }

  public setFormDirtyStateChanged(isDirty: boolean): void {
    this.healthHistoryDialogService.setFormDirtyState(isDirty);
  }

  public addNewPatientAlertsItem(): void {
    const newItemTemporaryId = -1;

    this.store.dispatch(
      patientAlertsActions.addNewTemporaryItem({
        patientAlert: {
          id: newItemTemporaryId,
          patientId: Number.parseInt(this.patientId()),
          description: '',
          noteText: '',
          startDate: undefined,
          endDate: undefined,
          createDate: DateTime.now().toISODate(),
          isAlways: false,
          showApptSymbol: false,
          showInPatientChart: true,
          showInSchedule: true,
          provisionalStatus: ProvisionalStatus.READY,
        },
      }),
    );
  }

  public discardNewNewPatientAlertsItem(): void {
    this.store.dispatch(patientAlertsActions.discardNewTemporaryItem());
  }

  public deletePatientAlertsItem(id: number): void {
    const item = this.patientAlerts().find((item) => item.id === id);

    if (!item) {
      return;
    }

    this.store.dispatch(
      patientAlertsActions.deletePatientAlertsItem({
        siteId: this.siteId(),
        patientId: this.patientId(),
        patientAlertsItem: item,
      }),
    );
  }

  public editPatientAlertsItem(data: PatientAlertsItemSaveData): void {
    if (data.isNewItem) {
      this.store.dispatch(
        patientAlertsActions.addPatientAlertsItem({
          siteId: this.siteId(),
          patientId: this.patientId(),
          patientAlertsItem: data.patientAlert,
        }),
      );
    } else {
      this.store.dispatch(
        patientAlertsActions.updatePatientAlertsItem({
          siteId: this.siteId(),
          patientId: this.patientId(),
          patientAlertsItem: data.patientAlert,
        }),
      );
    }
  }
}
